<template>
  <main class="snapshot-page">
    <section class="snapshot-card" aria-live="polite">
      <div class="brand">happily.ai</div>
      <p v-if="loading">{{ copy.loading }}</p>
      <p v-else-if="!snapshot" role="alert">{{ copy.unavailable }}</p>
      <template v-else>
        <div class="eyebrow">{{ snapshot.cumulative ? copy.cumulative : copy.daily }}</div>
        <h1>{{ question }}</h1>
        <p class="meta">{{ copy.sent }} {{ snapshot.questionDate }} · {{ copy.asOf }} {{ snapshot.reportDate }}</p>
        <div class="score" :style="{ color: scoreColor }">{{ score }}<small>{{ snapshot.type === 'Scale' ? '/10' : '%' }}</small></div>
        <p class="scope">{{ snapshot.scope === 'direct' ? copy.direct : copy.org }}</p>
        <p>{{ snapshot.total }} {{ copy.total }} · {{ snapshot.newCount }} {{ copy.new }} {{ snapshot.reportDate }}</p>
        <p class="note">{{ copy.frozen }}</p>
        <h2>{{ copy.breakdown }}</h2>
        <div v-for="(row, i) in rows" :key="i" class="answer-row">
          <div class="answer-label"><span>{{ row.label }}</span><span>{{ row.count }} · {{ row.percent }}%</span></div>
          <div class="track"><div class="fill" :style="{ width: row.percent + '%' }"></div></div>
        </div>
      </template>
    </section>
  </main>
</template>
<script>
import axios from 'axios';
import { breakdown } from './question-details/snapshot';
const strings = {
  en: { loading: 'Loading report…', unavailable: 'This saved report is unavailable. We cannot show a matching breakdown for this email.', cumulative: 'Cumulative question result', daily: 'Question result', sent: 'Question sent', asOf: 'Results as of', direct: 'My direct reports', org: 'Org-wide', total: 'responses total', new: 'new on', frozen: 'This is the saved result from your email. Later responses are not included.', breakdown: 'Response breakdown' },
  th: { loading: 'กำลังโหลดรายงาน…', unavailable: 'ไม่พบรายงานที่บันทึกไว้ จึงไม่สามารถแสดงรายละเอียดที่ตรงกับอีเมลนี้ได้', cumulative: 'ผลคำตอบสะสม', daily: 'ผลคำตอบ', sent: 'ส่งคำถามเมื่อ', asOf: 'ผล ณ วันที่', direct: 'ลูกทีมที่ขึ้นตรงกับฉัน', org: 'ทั้งองค์กร', total: 'คำตอบทั้งหมด', new: 'คำตอบใหม่วันที่', frozen: 'นี่คือผลที่บันทึกไว้ในอีเมลของคุณ ไม่รวมคำตอบที่ได้รับหลังจากนั้น', breakdown: 'รายละเอียดคำตอบ' },
  ms: { loading: 'Memuatkan laporan…', unavailable: 'Laporan tersimpan ini tidak tersedia. Pecahan yang sepadan dengan e-mel ini tidak dapat dipaparkan.', cumulative: 'Keputusan soalan terkumpul', daily: 'Keputusan soalan', sent: 'Soalan dihantar', asOf: 'Keputusan setakat', direct: 'Laporan terus saya', org: 'Seluruh organisasi', total: 'maklum balas keseluruhan', new: 'baharu pada', frozen: 'Ini ialah keputusan tersimpan daripada e-mel anda. Maklum balas kemudian tidak disertakan.', breakdown: 'Pecahan maklum balas' }
};
export default {
  data: () => ({ loading: true, snapshot: null, lang: 'en' }),
  computed: {
    copy() { return strings[this.lang] || strings.en; },
    question() { return this.snapshot.question[this.lang] || this.snapshot.questionEn; },
    score() { return this.snapshot.type === 'Scale' ? (Math.round(this.snapshot.score * 10) / 10).toFixed(1) : String(Math.round(this.snapshot.score)); },
    scoreColor() { return { red: '#D64550', orange: '#C9820A', gray: '#6B6575', green: '#1F9D6B' }[this.snapshot.color] || '#6B6575'; },
    rows() { return breakdown(this.snapshot, this.lang); }
  },
  mounted() { this.load(); },
  watch: { '$route.fullPath'() { this.load(); } },
  methods: {
    async load() {
      const path = this.$route.fullPath;
      this.loading = true; this.snapshot = null;
      try {
        const id = String(this.$route.query.snapshot || '');
        if (!/^[a-f0-9]{64}$/.test(id)) return;
        const response = await axios.post('https://us-central1-people2-admin.cloudfunctions.net/getDailyInsightSnapshot', {
          token: this.$route.params.id, snapshot: id
        }, { timeout: 20000 });
        if (this.$route.fullPath !== path) return;
        const result = response.data;
        if (!result || !result.snapshot || result.snapshot.id !== id) return;
        this.lang = result.language || 'en';
        this.snapshot = result.snapshot;
      } catch (error) {
        // Do not fall through to a daily/live result when the exact snapshot is unavailable.
        this.snapshot = null;
      } finally { if (this.$route.fullPath === path) this.loading = false; }
    }
  }
};
</script>
<style scoped>
.snapshot-page{background:#f4f4f6;min-height:100vh;padding:40px 16px;box-sizing:border-box;color:#211e2a}
.snapshot-card{max-width:720px;margin:auto;background:white;border-radius:12px;padding:28px 32px;box-shadow:0 6px 24px #0000000f}
.brand{font-weight:900;color:#6d27bf;margin-bottom:24px}.eyebrow{font-size:12px;text-transform:uppercase;letter-spacing:.08em;color:#6d27bf}
h1{font-size:24px;line-height:1.35;margin:8px 0}.meta,.note{font-size:13px;color:#6b6575;line-height:1.5}.score{font-size:42px;font-weight:800;margin-top:24px}.score small{font-size:18px}.scope{font-weight:700;margin:4px 0}h2{font-size:15px;border-top:1px solid #e7e3ef;padding-top:24px;margin-top:24px}.answer-row{margin:16px 0}.answer-label{display:flex;justify-content:space-between;gap:16px;font-size:13px;margin-bottom:6px}.answer-label span:last-child{white-space:nowrap}.track{height:12px;background:#f3ebfb;border-radius:6px;overflow:hidden}.fill{height:100%;background:#8d629a;border-radius:6px}
@media(max-width:639px){.snapshot-page{padding:16px 10px}.snapshot-card{padding:24px 20px}h1{font-size:20px}}
</style>
